import React from "react";

import { ArrayUtils } from "utils";

import { Collapse, Icon, Typography } from "components";

export const PageStatusToIconColor = {
  complete: "green",
  incomplete: "red",
};
export type PageStatus = keyof typeof PageStatusToIconColor | undefined;
export type FormFlowNavBarIconName = "checkmark" | "warning" | "dot" | null;

interface FormFlowNavBarIconProps {
  iconName?: FormFlowNavBarIconName;
  pageStatus?: PageStatus | null;
}

const FormFlowNavBarIcon = ({ iconName = "checkmark", pageStatus }: FormFlowNavBarIconProps) => {
  const iconColor = pageStatus ? PageStatusToIconColor[pageStatus] : "grey";

  if (iconName === "dot") {
    return <Icon className={`icon ${iconColor} dot`} iconName="tabler/circle" strokeWidth="1px" size={12} fill="currentColor" />;
  }

  if (iconName === "warning" || pageStatus === "incomplete") {
    return <Icon className={`icon ${iconColor}`} iconName="tabler/alert-circle-filled" fill="currentColor" />;
  }

  return <Icon className={`icon ${iconColor}`} iconName="tabler/circle-check-filled" />;
};

export interface FormFlowNavBarItemProps {
  title: string;
  pageNumber: number;
  indent?: boolean;
  active?: boolean;
  iconName?: FormFlowNavBarIconName;
  pageStatus?: PageStatus | null;
  handleSetActivePageNumber: (i: number) => void;
}

const FormFlowNavBarPageEntry = ({ active, title, iconName, pageNumber, handleSetActivePageNumber, pageStatus, indent }: FormFlowNavBarItemProps) => (
  <div
    className={`full-screen-overlay-nav-item clickable footprinting-nav-step-item ${active ? "active" : ""} ${indent ? "indent" : ""}`}
    onClick={() => handleSetActivePageNumber(pageNumber)}
  >
    <FormFlowNavBarIcon iconName={iconName} pageStatus={pageStatus} />
    <Typography variant="paragraph-m">{title}</Typography>
  </div>
);

interface FormFlowNavBarCategoryProps {
  items: FormFlowNavBarItemProps[];
  category: string;
  activePageNumber: number;
}

const FormFlowNavBarCategoryEntry = ({ items, category, activePageNumber }: FormFlowNavBarCategoryProps) => {
  const hasIncomplete = items.some((i) => i.pageStatus === "incomplete");
  const allComplete = items.every((i) => i.pageStatus === "complete");

  const categoryIconProps: FormFlowNavBarIconProps = allComplete
    ? { iconName: "checkmark", pageStatus: "complete" }
    : hasIncomplete
    ? { iconName: "checkmark", pageStatus: "incomplete" }
    : { iconName: "checkmark", pageStatus: undefined };

  if (items.length < 1) {
    return null;
  }

  const isActive = items.some((i) => i.active);

  return (
    <>
      <Collapse
        className="form-flow-nav-bar-collapse"
        activeKey={isActive ? category : undefined}
        items={[
          {
            key: category,
            label: (
              <div className="full-screen-overlay-nav-item clickable" onClick={() => items[0].handleSetActivePageNumber(items[0].pageNumber)}>
                <FormFlowNavBarIcon {...categoryIconProps} />
                <Typography variant="paragraph-m">{category}</Typography>
              </div>
            ),
            children: items.map((i) => (
              <FormFlowNavBarPageEntry key={`${category}_${i.title}`} {...i} active={activePageNumber === i.pageNumber} iconName={"dot"} indent />
            )),
          },
        ]}
      />
    </>
  );
};

interface FormFlowNavBarPageEntry {
  title: string;
  category?: string;
  status: PageStatus | null;
}

export type FormFlowNavBarProps = {
  pages: FormFlowNavBarPageEntry[];
  activePageNumber: number;
  handleSetActivePageNumber: (i: number) => void;
  title?: string;
};

export const FormFlowNavBar = ({ pages, activePageNumber, handleSetActivePageNumber, title }: FormFlowNavBarProps) => {
  const collectedItems = ArrayUtils.collectBy(
    pages.map((page, i) => ({ ...page, pageNumber: i, active: i === activePageNumber })),
    (i) => i.category ?? i.title
  )
    .filter((list) => list.length > 0)
    .map((l) => (l.length === 1 ? l[0] : l));

  return (
    <nav className="full-screen-overlay-nav footprinting-screen-overlay-nav">
      {title ? <Typography variant="h5">{title}</Typography> : null}
      <div className="full-screen-overlay-nav-items">
        {collectedItems.map((pageOrCategory) => {
          if (Array.isArray(pageOrCategory)) {
            const category = pageOrCategory[0].category ?? "";
            return (
              <FormFlowNavBarCategoryEntry
                key={category}
                category={category}
                activePageNumber={activePageNumber}
                items={pageOrCategory.map(
                  (page): FormFlowNavBarItemProps => ({
                    handleSetActivePageNumber,
                    title: page.title,
                    pageNumber: page.pageNumber,
                    active: page.active,
                    pageStatus: page.status,
                  })
                )}
              />
            );
          }

          return (
            <FormFlowNavBarPageEntry
              handleSetActivePageNumber={handleSetActivePageNumber}
              active={pageOrCategory.active}
              title={pageOrCategory.title}
              key={`${pageOrCategory.title}`}
              pageNumber={pageOrCategory.pageNumber}
              pageStatus={pageOrCategory.status}
            />
          );
        })}
      </div>
    </nav>
  );
};
